import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { CheckCircle, Gift, User, MapPin, Clock, AlertCircle, ChevronRight, Sparkles, PartyPopper, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiClient } from "@/lib/api-client";
import { queryClient } from "@/lib/queryClient";
import { useIdComply } from "@/hooks/useIdComply";
import { useAuth } from "@/contexts/AuthContext";
import { useVerification } from "@/contexts/VerificationContext";
import { subYears, isAfter, differenceInYears } from "date-fns";
import { getStateName, getAllStateNames } from "@/lib/us-states";
import { GCBadgeSVG, SCBadgeSVG } from "@/components/CurrencyBadge";

// Lazy load AddressAutocomplete - Google Maps SDK (~250KB) only loads when user opens this modal
const AddressAutocomplete = lazy(() => import("@/components/AddressAutocomplete").then(m => ({ default: m.AddressAutocomplete })));

interface JoiningBonusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileSaved?: (profileData: ProfileData) => void;
}

interface ProfileData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
}

export const JoiningBonusModal = ({ isOpen, onClose, onProfileSaved }: JoiningBonusModalProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { performDataVerification, getVerificationStatus, isVerifying } = useIdComply();
  const { refreshVerificationStatus } = useVerification();
  const [step, setStep] = useState<'verify' | 'claim' | 'success'>('verify');
  const [formStep, setFormStep] = useState<'personal' | 'address'>('personal');
  const [isUserVerified, setIsUserVerified] = useState(false);
  const [joiningBonusAmounts, setJoiningBonusAmounts] = useState({ gc: 10000, sc: 1 });
  const [hasStartedAddressSearch, setHasStartedAddressSearch] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  });
  
  // IDComply verification status (same structure as ProfilePage)
  const [idComplyVerificationStatus, setIdComplyVerificationStatus] = useState<{
    dataVerificationStatus: string;
    idVerificationStatus: string;
    dataVerificationAttempts?: number;
    idVerificationAttempts?: number;
    canAttemptDataVerification?: boolean;
    canAttemptIdVerification?: boolean;
    isPendingManualReview?: boolean;
  } | null>(null);

  // Track which fields have errors from last verification attempt
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());

  // Calculate max date (21 years ago) - must be at least 21 years old
  const maxDate = subYears(new Date(), 21);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const countries = [
    { value: 'US', label: 'United States' },
  ];

  const states = getAllStateNames();

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user changes the field
    if (fieldErrors.has(field)) {
      setFieldErrors(prev => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
    }
  };

  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      // Format date using local time to avoid timezone shifts
      const formattedDate = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
      setProfileData(prev => ({ ...prev, dateOfBirth: formattedDate }));
      // Clear date-related errors when user changes the date
      setFieldErrors(prev => {
        const next = new Set(prev);
        next.delete('dateOfBirth');
        next.delete('dobYear');
        next.delete('dobMonth');
        next.delete('dobDay');
        return next;
      });
    }
  };

  const isProfileComplete = () => {
    const { addressLine2, ...requiredFields } = profileData;
    return Object.values(requiredFields).every(value => value.trim() !== '');
  };

  const isPersonalInfoComplete = () => {
    return profileData.firstName.trim() !== '' && 
           profileData.lastName.trim() !== '' && 
           profileData.dateOfBirth.trim() !== '' &&
           profileData.gender.trim() !== '';
  };

  // Helper to get error styling for a field
  const getFieldErrorClass = (field: string) => {
    if (fieldErrors.has(field)) {
      return 'border-red-500/50 focus:border-red-400 ring-1 ring-red-500/30';
    }
    return '';
  };

  // Helper function to determine if fields should be locked (same as ProfilePage)
  // Fields are locked if: profile is verified OR 3 data verification attempts have been made
  const shouldLockFields = () => {
    return isUserVerified || 
           (idComplyVerificationStatus && !idComplyVerificationStatus.canAttemptDataVerification);
  };

  // Load IDComply verification status (same as ProfilePage)
  const loadVerificationStatus = async () => {
    try {
      const status = await getVerificationStatus();
      if (status) {
        setIdComplyVerificationStatus({
          dataVerificationStatus: status.dataVerificationStatus || 'not_started',
          idVerificationStatus: status.idVerificationStatus || 'not_started',
          dataVerificationAttempts: status.dataVerificationAttempts || 0,
          idVerificationAttempts: status.idVerificationAttempts || 0,
          canAttemptDataVerification: status.canAttemptDataVerification !== false,
          canAttemptIdVerification: status.canAttemptIdVerification !== false,
          isPendingManualReview: status.isPendingManualReview || false,
        });
        
        // Update isUserVerified based on verification status
        if (status.dataVerificationStatus === 'complete' || status.dataVerificationStatus === 'completed') {
          setIsUserVerified(true);
          setFormStep('personal'); // Reset form step when verified
          // If verified and on verify step, move to claim step
          if (step === 'verify') {
            setStep('claim');
          }
        }
      }
    } catch {
      // Silently fail - verification status check is not critical
    }
  };

  const handleVerifyProfile = async () => {
    // Check attempt limit before attempting data verification
    if (idComplyVerificationStatus && !idComplyVerificationStatus.canAttemptDataVerification) {
        toast({
          title: 'Maximum Attempts Exceeded',
          description: 'You have reached the maximum number of data verification attempts (3). Please contact support.',
          variant: 'destructive',
        });
        return;
      }

    if (!isProfileComplete()) {
      toast({
        title: "Incomplete Profile",
        description: "Please fill in all required fields to proceed.",
        variant: "destructive",
      });
      return;
    }

    const today = new Date();
    if (selectedDate) {
      if (isAfter(selectedDate, today)) {
        toast({
          title: "Invalid Date of Birth",
          description: "Date of birth cannot be in the future.",
          variant: "destructive",
        });
        return;
      }
      if (differenceInYears(today, selectedDate) < 21) {
        toast({
          title: "Invalid Date of Birth",
          description: "You must be at least 21 years old.",
          variant: "destructive",
        });
        return;
      }
    }

    // Perform data verification with IDComply (same flow as ProfilePage)
    // Backend will save profile data automatically if verification succeeds
    if (profileData.dateOfBirth) {
    try {
        // Clear previous field errors before new attempt
        setFieldErrors(new Set());
        
        const verificationResult = await performDataVerification({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          dateOfBirth: profileData.dateOfBirth,
          streetAddress: profileData.addressLine1,
          city: profileData.city,
          state: profileData.state,
          zip: profileData.postalCode,
          country: profileData.country,
          addressLine2: profileData.addressLine2 || undefined,
        });

        // Always reload verification status after any verification attempt
        // This ensures UI updates with latest attempt counts and lock status
        await loadVerificationStatus();

        // Handle result based on action from backend
        const action = verificationResult?.action || 'clear_form';
        
        if (action === 'success' || action === 'partial_success') {
          // Verification successful (full match or acceptable partial match)
          setIsUserVerified(true);
          setFormStep('personal');
          setStep('claim');
          setFieldErrors(new Set());
          
          // Refresh global verification status so user can purchase/play games immediately
          await refreshVerificationStatus();
          
          toast({
            title: "Profile Verified!",
            description: "Your profile has been successfully verified and saved.",
            variant: "success",
            duration: 5000,
          });
        } else if (action === 'clear_fields') {
          // Specific fields failed - highlight and clear only those fields
          const failedFields = verificationResult?.failedFields || verificationResult?.details?.filter((d: any) => d.match === 'fail') || [];
          const failedFieldNames = new Set<string>();
          
          // Map IDComply field names to our form field names
          const fieldMap: Record<string, string> = {
            'streetAddress': 'addressLine1',
            'zip': 'postalCode',
            'dobYear': 'dateOfBirth',
            'dobMonth': 'dateOfBirth',
            'dobDay': 'dateOfBirth',
          };
          
          failedFields.forEach((d: any) => {
            const formField = fieldMap[d.field] || d.field;
            failedFieldNames.add(formField);
          });
          
          // Clear the failed fields in the form
          setProfileData(prev => {
            const updated = { ...prev };
            failedFieldNames.forEach(field => {
              if (field === 'dateOfBirth') {
                updated.dateOfBirth = '';
              } else if (field in updated) {
                (updated as any)[field] = '';
              }
            });
            return updated;
          });
          
          // Clear the date picker if DOB failed
          if (failedFieldNames.has('dateOfBirth')) {
            setSelectedDate(undefined);
          }
          
          setFieldErrors(failedFieldNames);
          
          // Navigate to the step with errors
          const personalFields = ['firstName', 'lastName', 'dateOfBirth'];
          const hasPersonalFieldError = personalFields.some(f => failedFieldNames.has(f));
          setFormStep(hasPersonalFieldError ? 'personal' : 'address');
          
          toast({
            title: "Verification Failed",
            description: "Please correct the highlighted fields and try again.",
            variant: "destructive",
          });
        } else {
          // clear_form - No specific field info, clear entire form
          setProfileData({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            gender: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            country: '',
            postalCode: ''
          });
          setSelectedDate(undefined);
          setFieldErrors(new Set());
          setFormStep('personal');
          
          toast({
            title: "Verification Failed",
            description: "We couldn't verify your information. Please re-enter your details carefully.",
            variant: "destructive",
          });
        }
      } catch (error: any) {
        // Extract failed fields from error if available
        if (error.failedFields && Array.isArray(error.failedFields) && error.failedFields.length > 0) {
          const failedFieldNames = new Set<string>();
          const fieldMap: Record<string, string> = {
            'streetAddress': 'addressLine1',
            'zip': 'postalCode',
            'dobYear': 'dateOfBirth',
            'dobMonth': 'dateOfBirth',
            'dobDay': 'dateOfBirth',
          };
          
          error.failedFields.forEach((d: any) => {
            const formField = fieldMap[d.field] || d.field;
            failedFieldNames.add(formField);
          });
          
          // Clear the failed fields in the form
          setProfileData(prev => {
            const updated = { ...prev };
            failedFieldNames.forEach(field => {
              if (field === 'dateOfBirth') {
                updated.dateOfBirth = '';
              } else if (field in updated) {
                (updated as any)[field] = '';
              }
            });
            return updated;
          });
          
          // Clear the date picker if DOB failed
          if (failedFieldNames.has('dateOfBirth')) {
            setSelectedDate(undefined);
          }
          
          setFieldErrors(failedFieldNames);
          
          // Navigate to the step with errors
          const personalFields = ['firstName', 'lastName', 'dateOfBirth'];
          const hasPersonalFieldError = personalFields.some(f => failedFieldNames.has(f));
          setFormStep(hasPersonalFieldError ? 'personal' : 'address');
        } else {
          // No specific field errors - clear entire form
          setProfileData({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            gender: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            state: '',
            country: '',
            postalCode: ''
          });
          setSelectedDate(undefined);
          setFieldErrors(new Set());
          setFormStep('personal');
        }
        
        // Always reload verification status after any error
        // This ensures UI updates with latest attempt counts and lock status
        await loadVerificationStatus();
      }
    } else {
      toast({
        title: "Date of Birth Required",
        description: "Please enter your date of birth to verify your profile.",
        variant: "destructive",
      });
    }
  };

  const handleClaimBonus = async () => {
    try {
      const data = await apiClient.post('/joining-bonus/claim');

      if (!data?.success) {
        toast({
          title: "Claim Error",
          description: "Failed to claim bonus. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setStep('success');
      // Invalidate user init cache so balance and joiningBonus status refresh
      queryClient.invalidateQueries({ queryKey: ['user', 'init'] });
      toast({
        title: "Joining Bonus Claimed!",
        description: `${joiningBonusAmounts.gc.toLocaleString()} GC + ${joiningBonusAmounts.sc} SC has been added to your account!`,
        variant: "success",
      });
    } catch {
      toast({
        title: "Claim Error",
        description: "Failed to claim bonus. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setStep(isUserVerified ? 'claim' : 'verify');
    setFormStep('personal'); // Reset to first step when closing
    onClose();
  };

  // Load verification status and existing profile data
  useEffect(() => {
    if (!isOpen || !user) return;
    
    const loadUserStatus = async () => {
      try {
        // Load verification status (same as ProfilePage)
        await loadVerificationStatus();

        // Check profile verification status via backend
        const data = await apiClient.get('/joining-bonus/profile-verification-status');

        // Validate response format - must have success: true
        if (!data?.success) {
          return;
        }

        // Get joining bonus amounts from the response
        if (data.gcAmount && data.scAmount) {
          setJoiningBonusAmounts({ gc: data.gcAmount, sc: data.scAmount });
        }

        // Handle profile data (can be null if user hasn't created profile yet)
        if (data.profile) {
          // Load existing profile data
          const loadedProfileData = {
            firstName: data.profile.first_name || '',
            lastName: data.profile.last_name || '',
            dateOfBirth: data.profile.date_of_birth || '',
            gender: data.profile.gender || '',
            addressLine1: data.profile.address_line_1 || '',
            addressLine2: data.profile.address_line_2 || '',
            city: data.profile.city || '',
            state: data.profile.state_province || '',
            country: data.profile.country || '',
            postalCode: data.profile.postal_code || ''
          };
          setProfileData(loadedProfileData);

          // Show address fields if address data already exists
          if (loadedProfileData.addressLine1 || loadedProfileData.city || loadedProfileData.state) {
            setHasStartedAddressSearch(true);
          }

          // Set the date picker if date of birth exists
          if (data.profile.date_of_birth) {
            // Parse date in UTC to avoid timezone issues
            const dateStr = data.profile.date_of_birth;
            const parsedDate = new Date(dateStr + 'T00:00:00.000Z');
            setSelectedDate(parsedDate);
          }

          // If user is already verified, skip to claim step
          if (idComplyVerificationStatus?.dataVerificationStatus === 'complete' || 
              idComplyVerificationStatus?.dataVerificationStatus === 'completed') {
            setIsUserVerified(true);
            setStep('claim');
          }
        } else {
          // User doesn't have a profile yet - start with verify step
          setIsUserVerified(false);
          setStep('verify');
        }
      } catch {
        // Don't show toast here - this is a background check that shouldn't interrupt the user
      }
    };

    loadUserStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // Full-screen dialog class matching RedeemModal / DailyBonusModal pattern
  // Mobile: the dialog itself doesn't need bottom padding - the content container handles scroll padding
  // Desktop: overflow-visible allows address autocomplete dropdown to extend beyond modal bounds
  const fullScreenDialogClass = "!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-md lg:!h-auto lg:!max-h-none m-0 p-0 rounded-none lg:rounded-2xl border-0 lg:border lg:border-amber-500/20 bg-transparent overflow-hidden lg:overflow-visible !translate-x-0 !translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 lg:shadow-[0_0_60px_rgba(251,191,36,0.15),0_25px_50px_rgba(0,0,0,0.5)]";

  const contentScrollRef = useRef<HTMLDivElement>(null);
  const addressSearchRef = useRef<HTMLDivElement>(null);

  // When switching to address step, scroll the search field to the top so
  // the Google autocomplete dropdown has enough room below it on mobile
  useEffect(() => {
    if (formStep === 'address' && addressSearchRef.current && contentScrollRef.current) {
      // Small delay to let the DOM update after the step change
      setTimeout(() => {
        addressSearchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [formStep]);

  // Verification status helpers
  const verificationStatusLabel = (() => {
    const status = idComplyVerificationStatus?.dataVerificationStatus;
    if (status === 'complete' || status === 'completed') return 'Verified';
    if (status === 'in_progress') return 'In Progress';
    if (status === 'pending') return 'Pending';
    if (status === 'failed') return 'Failed';
    return 'Not Started';
  })();

  const verificationStatusColor = (() => {
    const status = idComplyVerificationStatus?.dataVerificationStatus;
    if (status === 'complete' || status === 'completed') return 'green';
    if (status === 'in_progress' || status === 'pending') return 'yellow';
    if (status === 'failed') return 'red';
    return 'blue';
  })();

  const isVerificationComplete = idComplyVerificationStatus?.dataVerificationStatus === 'complete' || idComplyVerificationStatus?.dataVerificationStatus === 'completed';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent 
        className={fullScreenDialogClass} 
        aria-describedby={undefined}
      >
        {/* Full background wrapper - overflow-visible on desktop for address dropdown */}
        <div className="w-full h-full lg:h-auto bg-card overflow-hidden lg:overflow-visible relative flex flex-col lg:rounded-2xl">
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 lg:top-4 lg:right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          
          {/* Radial glow accents */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[600px]">
              <div className="w-full h-full rounded-full bg-gradient-radial from-amber-500/15 via-amber-600/5 to-transparent blur-3xl" />
            </div>
            <div className="absolute bottom-0 right-0 w-[400px] h-[400px]">
              <div className="w-full h-full rounded-full bg-gradient-radial from-purple-500/10 via-purple-600/5 to-transparent blur-3xl" />
            </div>
          </div>

          {/* Content — uses margin-auto centering instead of justify-center to allow proper scrolling on mobile */}
          {/* Mobile: pb accounts for bottom nav (~5rem) + safe area so content scrolls past the nav bar */}
          {/* Desktop: overflow-visible allows address autocomplete dropdown to extend beyond modal */}
          <div ref={contentScrollRef} className="relative flex-1 flex flex-col items-center px-5 lg:px-6 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-6 pt-2 lg:pt-6 min-h-0 overflow-y-auto lg:overflow-visible">

            {!isUserVerified && step === 'verify' && (
              <div className={`space-y-4 lg:space-y-5 w-full max-w-sm md:max-w-lg ${formStep === 'address' ? 'mt-2' : 'my-auto shrink-0'}`}>
                {/* Hero Bonus Preview */}
                <div className="relative text-center pt-2">
                  {/* Hide icon on address step on mobile to make room for form fields + verify button */}
                  <div className={`relative inline-block mb-3 short-screen-hidden ${formStep === 'address' ? 'hidden lg:inline-block' : ''}`}>
                    <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-[0_0_30px_rgba(251,191,36,0.4)] mx-auto">
                      <Gift className="h-8 w-8 lg:h-10 lg:w-10 text-amber-900" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg">
                      <Sparkles className="h-3.5 w-3.5 text-white" />
                    </div>
                  </div>
                  <h2 className="text-xl md:text-2xl lg:text-2xl font-black tracking-tight bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent">
                    Joining Bonus
                  </h2>
                  <p className="text-sm md:text-base text-white/60 mt-1">
                    Complete your profile to claim
                  </p>

                  {/* Bonus Amount Cards */}
                  <div className="grid grid-cols-2 gap-3 mt-4 max-w-xs md:max-w-sm mx-auto">
                    <div className="flex items-center gap-2 bg-amber-500/15 backdrop-blur-sm border border-amber-400/30 rounded-xl px-3 py-2.5">
                      <GCBadgeSVG size={26} />
                      <span className="text-lg font-bold text-amber-200">{joiningBonusAmounts.gc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-500/15 backdrop-blur-sm border border-emerald-400/30 rounded-xl px-3 py-2.5">
                      <SCBadgeSVG size={26} />
                      <span className="text-lg font-bold text-emerald-200">{joiningBonusAmounts.sc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormStep('personal')}
                    className="flex items-center gap-2 transition-all"
                    disabled={shouldLockFields()}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      formStep === 'personal'
                        ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 shadow-[0_0_12px_rgba(251,191,36,0.4)]'
                        : 'bg-white/10 text-white/50 border border-white/20'
                    }`}>
                      1
                    </div>
                    <span className={`text-sm font-semibold ${formStep === 'personal' ? 'text-amber-200' : 'text-white/40'}`}>
                      Personal Info
                    </span>
                  </button>
                  <ChevronRight className="h-4 w-4 text-white/30" />
                  <button
                    type="button"
                    onClick={() => {
                      if (isPersonalInfoComplete()) {
                        setFormStep('address');
                      } else {
                        toast({
                          title: "Complete Personal Info",
                          description: "Please complete your personal information before proceeding to address.",
                          variant: "destructive",
                        });
                      }
                    }}
                    className="flex items-center gap-2 transition-all"
                    disabled={shouldLockFields() || !isPersonalInfoComplete()}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      formStep === 'address'
                        ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-amber-900 shadow-[0_0_12px_rgba(251,191,36,0.4)]'
                        : isPersonalInfoComplete()
                          ? 'bg-white/10 text-white/50 border border-white/20 hover:border-amber-400/40'
                          : 'bg-white/5 text-white/20 border border-white/10'
                    }`}>
                      2
                    </div>
                    <span className={`text-sm font-semibold ${formStep === 'address' ? 'text-amber-200' : 'text-white/40'}`}>
                      Address
                    </span>
                  </button>
                </div>

                {/* Step 1: Personal Information */}
                {formStep === 'personal' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-4 w-4 text-amber-400" />
                      <h4 className="font-semibold text-sm text-white/90">Personal Information</h4>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="firstName" className={`text-xs ${fieldErrors.has('firstName') ? 'text-red-400' : 'text-white/60'}`}>
                          First Name <span className="text-amber-400">*</span>
                        </Label>
                        <Input
                          id="firstName"
                          value={profileData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          placeholder="John"
                          className={`mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50 focus:ring-amber-400/20 ${getFieldErrorClass('firstName')}`}
                          disabled={shouldLockFields()}
                          required
                        />
                        {fieldErrors.has('firstName') && (
                          <p className="text-xs text-red-400 mt-1">Please check this field</p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="lastName" className={`text-xs ${fieldErrors.has('lastName') ? 'text-red-400' : 'text-white/60'}`}>
                          Last Name <span className="text-amber-400">*</span>
                        </Label>
                        <Input
                          id="lastName"
                          value={profileData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          placeholder="Doe"
                          className={`mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50 focus:ring-amber-400/20 ${getFieldErrorClass('lastName')}`}
                          disabled={shouldLockFields()}
                          required
                        />
                        {fieldErrors.has('lastName') && (
                          <p className="text-xs text-red-400 mt-1">Please check this field</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="dateOfBirth" className={`text-xs ${fieldErrors.has('dateOfBirth') ? 'text-red-400' : 'text-white/60'}`}>
                        Date of Birth <span className="text-amber-400">*</span>
                      </Label>
                      <div className={`mt-1 ${fieldErrors.has('dateOfBirth') ? '[&_button]:border-red-500/50 [&_button]:ring-1 [&_button]:ring-red-500/30' : ''}`}>
                        <DatePicker
                          date={selectedDate}
                          onSelect={(date) => {
                            if (!shouldLockFields()) {
                              handleDateChange(date);
                            }
                          }}
                          placeholder="Date of Birth"
                          disabledPicker={shouldLockFields()}
                          disabled={(date) => date > maxDate || date > new Date()}
                          fromYear={1940}
                          toYear={new Date().getFullYear() - 21}
                          variant="dark"
                          className="w-full"
                        />
                      </div>
                      {fieldErrors.has('dateOfBirth') && (
                        <p className="text-xs text-red-400 mt-1">Please check your date of birth</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-white/60">
                        Gender <span className="text-amber-400">*</span>
                      </Label>
                      <div className="grid grid-cols-2 gap-3 mt-1">
                        <button
                          type="button"
                          onClick={() => !shouldLockFields() && handleInputChange('gender', 'male')}
                          disabled={shouldLockFields()}
                          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${
                            profileData.gender === 'male'
                              ? 'bg-amber-500/20 border-amber-400/50 text-amber-200'
                              : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                          } ${shouldLockFields() ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className="text-lg">♂</span>
                          <span className="text-sm font-medium">Male</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => !shouldLockFields() && handleInputChange('gender', 'female')}
                          disabled={shouldLockFields()}
                          className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${
                            profileData.gender === 'female'
                              ? 'bg-amber-500/20 border-amber-400/50 text-amber-200'
                              : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:text-white/80'
                          } ${shouldLockFields() ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className="text-lg">♀</span>
                          <span className="text-sm font-medium">Female</span>
                        </button>
                      </div>
                    </div>

                    {/* Verification Status Display */}
                    {idComplyVerificationStatus && (
                      <div className={`flex items-center gap-3 p-3 rounded-xl border backdrop-blur-sm ${
                        verificationStatusColor === 'green'
                          ? 'bg-green-500/10 border-green-500/20'
                          : verificationStatusColor === 'yellow'
                            ? 'bg-yellow-500/10 border-yellow-500/20'
                            : verificationStatusColor === 'red'
                              ? 'bg-red-500/10 border-red-500/20'
                              : 'bg-blue-500/10 border-blue-500/20'
                      }`}>
                        {isVerificationComplete ? (
                          <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                        ) : verificationStatusColor === 'yellow' ? (
                          <Clock className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                        ) : verificationStatusColor === 'red' ? (
                          <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                        ) : (
                          <Clock className="h-5 w-5 text-blue-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-xs text-white/80">Profile Data Verification</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              verificationStatusColor === 'green'
                                ? 'bg-green-500/30 text-green-300'
                                : verificationStatusColor === 'yellow'
                                  ? 'bg-yellow-500/30 text-yellow-300'
                                  : verificationStatusColor === 'red'
                                    ? 'bg-red-500/30 text-red-300'
                                    : 'bg-blue-500/30 text-blue-300'
                            }`}>
                              {verificationStatusLabel}
                            </span>
                          </div>
                          <p className="text-[11px] text-white/40 mt-0.5">
                            {isVerificationComplete
                              ? 'Your profile data has been verified'
                              : !idComplyVerificationStatus.canAttemptDataVerification
                                ? 'Maximum data verification attempts (3) exceeded. Please contact support.'
                                : verificationStatusColor === 'yellow'
                                  ? `Data verification is in progress (Attempt ${(idComplyVerificationStatus.dataVerificationAttempts || 0) + 1}/3)`
                                  : verificationStatusColor === 'red'
                                    ? `Verification failed. Attempts: ${idComplyVerificationStatus.dataVerificationAttempts || 0}/3`
                                    : `Complete your profile to start verification (${idComplyVerificationStatus.dataVerificationAttempts || 0}/3)`}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Next Button or Contact Support */}
                    {idComplyVerificationStatus && !idComplyVerificationStatus.canAttemptDataVerification ? (
                      <Button
                        onClick={() => window.location.href = 'mailto:support@goldmachine.com?subject=Profile Verification Support Request'}
                        className="w-full bg-white/10 hover:bg-white/15 text-white border border-white/20"
                        variant="outline"
                      >
                        Maximum Attempts Reached - Contact Support
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setFormStep('address')}
                        className="w-full h-11 text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_20px_rgba(251,191,36,0.3)] border-0"
                        disabled={!isPersonalInfoComplete() || shouldLockFields()}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    )}
                  </div>
                )}

                {/* Step 2: Address Information */}
                {formStep === 'address' && (
                  <div className="space-y-3">
                    <div ref={addressSearchRef} className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-amber-400" />
                      <h4 className="font-semibold text-sm text-white/90">Address Information</h4>
                    </div>

                    <div>
                      <Label htmlFor="address-search" className="text-xs text-white/60">
                        Search Address
                      </Label>
                      <div className="mt-1">
                        <Suspense fallback={<Input placeholder="Loading address search..." disabled className="bg-white/5 border-white/10" />}>
                          <AddressAutocomplete
                            id="address-search"
                            value=""
                            disabled={shouldLockFields()}
                            onChange={(address, addressData) => {
                              setHasStartedAddressSearch(true);
                              const updates: Partial<typeof profileData> = {};
                              if (addressData.streetAddress) updates.addressLine1 = addressData.streetAddress;
                              if (addressData.city) updates.city = addressData.city;
                              if (addressData.state) {
                                const stateName = getStateName(addressData.state);
                                updates.state = stateName;
                              }
                              if (addressData.zip) {
                                const zipWithoutSuffix = addressData.zip.split('-')[0];
                                updates.postalCode = zipWithoutSuffix;
                              }
                              if (addressData.country) updates.country = addressData.country;
                              if (Object.keys(updates).length > 0) {
                                setProfileData(prev => ({ ...prev, ...updates }));
                              }
                            }}
                            placeholder="Search for your address"
                            label=""
                            required={false}
                          />
                        </Suspense>
                      </div>
                    </div>

                    {hasStartedAddressSearch && (
                      <>
                        <div>
                          <Label htmlFor="addressLine1" className={`text-xs ${fieldErrors.has('addressLine1') ? 'text-red-400' : 'text-white/60'}`}>
                            Address Line 1 <span className="text-amber-400">*</span>
                          </Label>
                          <Input
                            id="addressLine1"
                            value={profileData.addressLine1}
                            onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                            placeholder="Address Line 1"
                            className={`mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50 focus:ring-amber-400/20 ${getFieldErrorClass('addressLine1')}`}
                            disabled={shouldLockFields()}
                            required
                          />
                          {fieldErrors.has('addressLine1') && (
                            <p className="text-xs text-red-400 mt-1">Please check your address</p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="addressLine2" className="text-xs text-white/60">
                            Address Line 2 <span className="text-white/30 text-[10px]">(Optional)</span>
                          </Label>
                          <Input
                            id="addressLine2"
                            value={profileData.addressLine2}
                            onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                            placeholder="Apt 4B, Suite 200, etc."
                            className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50 focus:ring-amber-400/20"
                            disabled={shouldLockFields()}
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="city" className={`text-xs ${fieldErrors.has('city') ? 'text-red-400' : 'text-white/60'}`}>
                              City <span className="text-amber-400">*</span>
                            </Label>
                            <Input
                              id="city"
                              value={profileData.city}
                              onChange={(e) => handleInputChange('city', e.target.value)}
                              placeholder="Los Angeles"
                              className={`mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50 focus:ring-amber-400/20 ${getFieldErrorClass('city')}`}
                              disabled={shouldLockFields()}
                              required
                            />
                            {fieldErrors.has('city') && (
                              <p className="text-xs text-red-400 mt-1">Please check city</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="state" className={`text-xs ${fieldErrors.has('state') ? 'text-red-400' : 'text-white/60'}`}>
                              State <span className="text-amber-400">*</span>
                            </Label>
                            <Select value={profileData.state} onValueChange={(value) => handleInputChange('state', value)} disabled={shouldLockFields()}>
                              <SelectTrigger className={`mt-1 bg-white/5 border-white/10 text-white ${fieldErrors.has('state') ? 'border-red-500/50 ring-1 ring-red-500/30' : ''}`}>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem key={state} value={state}>
                                    {state}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {fieldErrors.has('state') && (
                              <p className="text-xs text-red-400 mt-1">Please check state</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="postalCode" className={`text-xs ${fieldErrors.has('postalCode') ? 'text-red-400' : 'text-white/60'}`}>
                              Postal Code <span className="text-amber-400">*</span>
                            </Label>
                            <Input
                              id="postalCode"
                              value={profileData.postalCode}
                              onChange={(e) => handleInputChange('postalCode', e.target.value)}
                              placeholder="12345"
                              className={`mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-400/50 focus:ring-amber-400/20 ${getFieldErrorClass('postalCode')}`}
                              disabled={shouldLockFields()}
                              required
                            />
                            {fieldErrors.has('postalCode') && (
                              <p className="text-xs text-red-400 mt-1">Please check ZIP</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="country" className="text-xs text-white/60">
                              Country <span className="text-amber-400">*</span>
                            </Label>
                            <Select value={profileData.country} onValueChange={(value) => handleInputChange('country', value)} disabled={shouldLockFields()}>
                              <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white">
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                              <SelectContent>
                                {countries.map((country) => (
                                  <SelectItem key={country.value} value={country.value}>
                                    {country.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Verify Button */}
                    <Button
                      onClick={handleVerifyProfile}
                      className="w-full h-11 text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_20px_rgba(251,191,36,0.3)] border-0"
                      disabled={shouldLockFields() || !isProfileComplete() || isVerifying}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {shouldLockFields()
                        ? (idComplyVerificationStatus && !idComplyVerificationStatus.canAttemptDataVerification
                            ? 'Maximum Attempts Reached'
                            : 'Profile Locked')
                        : isVerifying
                          ? 'Verifying...'
                          : idComplyVerificationStatus?.dataVerificationStatus === 'failed'
                            ? 'Verify Data & Retry'
                            : 'Save & Verify Data'}
                    </Button>

                    {idComplyVerificationStatus && !idComplyVerificationStatus.canAttemptDataVerification && (
                      <Button
                        onClick={() => window.location.href = 'mailto:support@goldmachine.com?subject=Profile Verification Support Request'}
                        className="w-full bg-white/10 hover:bg-white/15 text-white border border-white/20"
                        variant="outline"
                      >
                        Contact Support
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {step === 'claim' && (
              <div className="my-auto shrink-0 flex flex-col items-center text-center py-6 lg:py-8 w-full max-w-md">
                {/* Success check icon */}
                <div className="relative mb-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center shadow-[0_0_40px_rgba(16,185,129,0.4)]">
                    <CheckCircle className="h-10 w-10 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg animate-bounce">
                    <Gift className="h-3.5 w-3.5 text-amber-900" />
                  </div>
                </div>

                <h2 className="text-2xl font-black bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent mb-2">
                  {isUserVerified ? 'Welcome Back!' : 'Profile Verified!'}
                </h2>
                <p className="text-sm text-white/50 mb-6 max-w-xs">
                  {isUserVerified
                    ? 'Thanks for being a verified member! Here\'s your special bonus.'
                    : 'Your profile has been verified. Claim your joining bonus!'}
                </p>

                {/* Bonus Display */}
                <div className="w-full max-w-xs bg-gradient-to-br from-amber-500/15 via-amber-600/10 to-purple-500/10 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-5 mb-6 shadow-[0_0_30px_rgba(251,191,36,0.1)]">
                  <p className="text-xs font-bold text-amber-300 uppercase tracking-wider mb-3">Your Joining Bonus</p>
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex flex-col items-center gap-1.5">
                      <GCBadgeSVG size={36} />
                      <span className="text-xl font-black text-amber-200">{joiningBonusAmounts.gc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-[10px] text-white/40 font-medium uppercase tracking-wider">Gold Coins</span>
                    </div>
                    <div className="w-px h-14 bg-white/10" />
                    <div className="flex flex-col items-center gap-1.5">
                      <SCBadgeSVG size={36} />
                      <span className="text-xl font-black text-emerald-200">{joiningBonusAmounts.sc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-[10px] text-white/40 font-medium uppercase tracking-wider">Sweep Coins</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleClaimBonus}
                  className="w-full max-w-xs h-12 text-base font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_30px_rgba(251,191,36,0.4)] border-0 rounded-xl"
                >
                  <Gift className="h-5 w-5 mr-2" />
                  Claim My Bonus
                </Button>
              </div>
            )}

            {step === 'success' && (
              <div className="my-auto shrink-0 flex flex-col items-center text-center py-6 lg:py-8 w-full max-w-md">
                {/* Celebration icon */}
                <div className="relative mb-4">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-[0_0_60px_rgba(251,191,36,0.5)] animate-pulse">
                    <PartyPopper className="h-12 w-12 text-amber-900" />
                  </div>
                  {/* Sparkle accents */}
                  <Sparkles className="absolute -top-2 -left-2 h-6 w-6 text-amber-300 animate-pulse" style={{ animationDelay: '200ms' }} />
                  <Sparkles className="absolute -top-1 -right-3 h-5 w-5 text-amber-400 animate-pulse" style={{ animationDelay: '500ms' }} />
                  <Sparkles className="absolute -bottom-1 -left-3 h-5 w-5 text-emerald-400 animate-pulse" style={{ animationDelay: '800ms' }} />
                </div>

                <h2 className="text-2xl font-black bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent mb-2">
                  Congratulations!
                </h2>
                <p className="text-sm text-white/50 mb-6 max-w-xs">
                  Your joining bonus has been added to your account!
                </p>

                {/* Bonus Amounts */}
                <div className="grid grid-cols-2 gap-3 mb-6 max-w-xs mx-auto">
                  <div className="flex items-center gap-2 bg-amber-500/15 backdrop-blur-sm border border-amber-400/30 rounded-xl px-3 py-3">
                    <GCBadgeSVG size={26} />
                    <span className="text-lg font-black text-amber-200">+{joiningBonusAmounts.gc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center gap-2 bg-emerald-500/15 backdrop-blur-sm border border-emerald-400/30 rounded-xl px-3 py-3">
                    <SCBadgeSVG size={26} />
                    <span className="text-lg font-black text-emerald-200">+{joiningBonusAmounts.sc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <p className="text-xs text-white/40 mb-6">
                  Start playing your favorite games with your bonus coins!
                </p>

                <Button
                  onClick={handleClose}
                  className="w-full max-w-xs h-12 text-base font-bold bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-amber-950 shadow-[0_0_30px_rgba(251,191,36,0.4)] border-0 rounded-xl"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Start Playing
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};